import { useState } from 'preact/hooks';
import { MAX_PLAYER_NAME_LENGTH, PLAYER_COLORS } from '../../core/settings';
import { ROOM_CODE_LENGTH, normalizeRoomCode } from '../../net/transport';
import { Panel } from '../components/Panel';
import { toast, ui } from '../store';
import { useServices } from '../context';

function Identity() {
  const { settings, updateSettings, net } = useServices();
  const { playerName, playerColor } = settings.value;
  return (
    <div class="identity">
      <label class="field">
        <span>Your name</span>
        <input
          value={playerName}
          maxLength={MAX_PLAYER_NAME_LENGTH}
          onChange={(e) => {
            updateSettings({ playerName: (e.target as HTMLInputElement).value });
            net.refreshIdentity();
          }}
        />
      </label>
      <div class="field">
        <span>Overalls</span>
        <div class="swatches" role="radiogroup" aria-label="Outfit colour">
          {PLAYER_COLORS.map((color) => (
            <button
              key={color}
              role="radio"
              aria-checked={color === playerColor}
              aria-label={`Colour ${color}`}
              class={`swatch ${color === playerColor ? 'active' : ''}`}
              style={{ background: color }}
              onClick={() => {
                updateSettings({ playerColor: color });
                net.refreshIdentity();
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function copy(text: string, what: string): void {
  navigator.clipboard
    ?.writeText(text)
    .then(() => toast(`📋 ${what} copied!`, 'good'))
    .catch(() => toast(`Couldn't copy — ${text}`));
}

function PlayerList() {
  return (
    <ul class="player-list">
      {ui.players.value.map((p) => (
        <li key={p.id}>
          <span class="dot" style={{ background: p.color }} /> {p.name}
          {p.id === 'host' && <span class="muted"> · host</span>}
        </li>
      ))}
    </ul>
  );
}

export function FriendsPanel() {
  const { net } = useServices();
  const [code, setCode] = useState(ui.pendingJoin.value ?? '');
  const status = ui.net.value;
  const error = ui.netError.value;

  return (
    <Panel title="Play with Friends" icon="👥">
      <Identity />

      {status.mode === 'solo' && (
        <div class="mp-options">
          <div class="mp-card">
            <h3>🌼 Host your farm</h3>
            <p>
              Open your homestead and get a code. Friends who join can plant, water, harvest and
              sell with you!
            </p>
            <button class="big-button" onClick={() => void net.hostFarm()}>
              Open my farm
            </button>
          </div>
          <form
            class="mp-card"
            onSubmit={(e) => {
              e.preventDefault();
              void net.joinFarm(code);
            }}
          >
            <h3>🧺 Visit a friend</h3>
            <p>Enter the {ROOM_CODE_LENGTH}-letter farm code your friend shared.</p>
            <div class="join-row">
              <input
                class="code-input"
                value={code}
                placeholder="BLOOM"
                aria-label="Farm code"
                maxLength={ROOM_CODE_LENGTH}
                autoCapitalize="characters"
                onInput={(e) => setCode(normalizeRoomCode((e.target as HTMLInputElement).value))}
              />
              <button class="big-button" type="submit" disabled={code.length !== ROOM_CODE_LENGTH}>
                Visit
              </button>
            </div>
          </form>
        </div>
      )}

      {status.mode === 'connecting' && (
        <p class="connecting">
          <span class="spinner" aria-hidden="true" />{' '}
          {status.role === 'host'
            ? 'Opening your farm gate…'
            : 'Walking over to your friend’s farm…'}
        </p>
      )}

      {status.mode === 'host' && (
        <div class="mp-live">
          <p>Your farm is open! Share this code:</p>
          <div class="room-code" aria-label={`Farm code ${status.code}`}>
            {status.code}
          </div>
          <div class="button-row">
            <button class="small-button" onClick={() => copy(status.code, 'Code')}>
              Copy code
            </button>
            <button
              class="small-button primary"
              onClick={() => copy(net.shareLink(status.code), 'Invite link')}
            >
              Copy invite link
            </button>
          </div>
          <h3>On the farm</h3>
          <PlayerList />
          <button class="small-button ghost" onClick={() => net.leave()}>
            Close my farm
          </button>
        </div>
      )}

      {status.mode === 'client' && (
        <div class="mp-live">
          <p>
            You're visiting <b>{status.hostName}</b>'s farm. Everything you grow and sell here helps
            them out!
          </p>
          <h3>On the farm</h3>
          <PlayerList />
          <button class="small-button ghost" onClick={() => net.leave()}>
            Go home
          </button>
        </div>
      )}

      {error && <p class="error">⚠️ {error}</p>}
      <p class="muted small">
        Multiplayer is peer-to-peer (WebRTC) — no accounts needed. Your own farm is saved safely
        while you visit others.
      </p>
    </Panel>
  );
}
